import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Camera, ChevronRight } from 'lucide-react';
import { useApp } from '../context';
import { uploadApi } from '../lib/api';
import { compressImageForUpload } from '../lib/imageUtils';
import { showToast } from '../lib/toast';
import ContactAvatar from './ContactAvatar';
import ImageCropModal from './ImageCropModal';

function formatBirthday(dateStr: string): string {
  if (!dateStr) return 'Not set';
  const [y, m, d] = dateStr.split('-');
  const date = new Date(+y, +m - 1, +d);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function PersonalInfo() {
  const navigate = useNavigate();
  const { state, setUser } = useApp();
  const [cropSrc, setCropSrc] = useState<string | null>(null);

  const user = state.user;

  const handlePhotoUpload = async (file: File) => {
    if (state.isGuestMode) {
      try {
        const { compressImage } = await import('../lib/imageUtils');
        const compressedBase64 = await compressImage(file, 250, 250, 0.6);
        if (user) setUser({ ...user, photoUrl: compressedBase64 });
      } catch {
        showToast('Failed to process image.');
      }
      return;
    }

    try {
      const compressed = await compressImageForUpload(file);
      const { url } = await uploadApi.photo(compressed);
      if (user) setUser({ ...user, photoUrl: url });
    } catch {
      showToast('Failed to upload photo. Please try again.');
    }
  };

  const fields = [
    { key: 'name', label: 'Name', value: user?.name || '' },
    { key: 'email', label: 'Email', value: user?.email || '' },
    {
      key: 'birthday',
      label: 'Birthday',
      value: user?.birthday ? formatBirthday(user.birthday) : 'Not set',
    },
  ];

  return (
    <div className="relative flex min-h-screen w-full flex-col bg-[#f8f5f5] overflow-x-hidden">
      {/* Header */}
      <div className="flex items-center p-4 pb-2 justify-between sticky top-0 bg-[#f8f5f5]/80 backdrop-blur-md z-10">
        <button
          onClick={() => navigate(-1)}
          className="size-10 rounded-full hover:bg-slate-200/50 flex items-center justify-center transition-colors"
        >
          <ArrowLeft size={24} className="text-slate-600" />
        </button>
        <h1 className="text-lg font-bold text-slate-900">Personal Information</h1>
        <div className="size-10" />
      </div>

      {/* Photo */}
      <div className="flex justify-center py-6">
        <label className="relative cursor-pointer group block rounded-full">
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onload = (ev) => setCropSrc(ev.target?.result as string);
              reader.readAsDataURL(file);
              e.target.value = '';
            }}
            className="hidden"
          />
          <div className="size-28 rounded-full border-4 border-white shadow-lg overflow-hidden relative">
            {user?.photoUrl ? (
              <img
                src={user.photoUrl}
                alt="Profile"
                className="w-full h-full object-cover transition-opacity group-hover:opacity-60"
                referrerPolicy="no-referrer"
              />
            ) : (
              <ContactAvatar
                name={user?.name || ''}
                size="xl"
                className="size-full transition-opacity group-hover:opacity-60"
              />
            )}
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <Camera size={24} className="text-white" />
            </div>
          </div>
          <div className="absolute bottom-0 right-0 size-9 rounded-full bg-[#fa8585] text-white flex items-center justify-center border-3 border-white shadow-md transition-transform group-active:scale-95">
            <Camera size={16} />
          </div>
        </label>
      </div>

      {/* Field Rows */}
      <div className="px-5 pb-8">
        <div className="rounded-2xl bg-white border border-slate-200 divide-y divide-slate-100 overflow-hidden">
          {fields.map((f) => (
            <button
              key={f.key}
              onClick={() => navigate(`/personal-info/edit/${f.key}`)}
              className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 active:bg-slate-100 transition-colors text-left"
            >
              <span className="text-sm font-semibold text-slate-900">{f.label}</span>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-400 truncate max-w-50">{f.value}</span>
                <ChevronRight size={16} className="text-slate-300 shrink-0" />
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Crop Modal */}
      {cropSrc && (
        <ImageCropModal
          imageSrc={cropSrc}
          onCancel={() => setCropSrc(null)}
          onCrop={(croppedFile) => {
            setCropSrc(null);
            handlePhotoUpload(croppedFile);
          }}
        />
      )}
    </div>
  );
}
